import React, { useState } from "react";
import { Select, SelectProps } from "@amzn/awsui-components-react";
import { Stage } from "../../lusca-search-api/generated-src";
import { PinnedAssignee, useAssigneeDirectory } from "./AssigneeAutosuggest";
import { UserAvatar } from "./UserAvatar";

/** Cloudscape's `iconUrl` would squash portrait badge photos; use the cropping SVG avatar. */
const Avatar: React.FC<{ alias: string }> = ({ alias }) => (
  <UserAvatar alias={alias} className="lusca-owner-avatar" />
);

export const ownerOption = (
  alias: string,
  description?: string
): SelectProps.Option => ({
  value: alias,
  label: alias,
  description,
  iconSvg: <Avatar alias={alias} />,
});

interface OwnerSelectProps {
  stage: Stage;
  /** Selected alias; null shows the placeholder. */
  value: string | null;
  onChange: (alias: string) => void;
  /** Aliases listed first (me, current owner, contributors), in order. */
  pinned?: PinnedAssignee[];
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  /** Load the user list straight away (e.g. in a modal) rather than on first focus. */
  loadImmediately?: boolean;
}

/**
 * The one owner picker: Lusca Search users with their photos in a searchable dropdown, like
 * Taskei's Assignee field. Used on the investigation page and behind "Needs an owner" in tables.
 */
export const OwnerSelect: React.FC<OwnerSelectProps> = ({
  stage,
  value,
  onChange,
  pinned = [],
  disabled,
  placeholder = "Unassigned",
  ariaLabel = "Owner",
  loadImmediately = false,
}) => {
  const [opened, setOpened] = useState(loadImmediately);
  const directory = useAssigneeDirectory(stage, opened);
  const pinnedAliases = new Set(pinned.map((p) => p.alias));
  const options: SelectProps.Options = [
    ...pinned.map((p) => ownerOption(p.alias, p.description)),
    ...directory.knownAliases
      .filter((alias) => !pinnedAliases.has(alias))
      .map((alias) => ownerOption(alias)),
  ];

  return (
    <Select
      ariaLabel={ariaLabel}
      selectedOption={value ? ownerOption(value) : null}
      triggerVariant="option"
      placeholder={placeholder}
      options={options}
      filteringType="auto"
      filteringPlaceholder="Search Lusca Search users"
      statusType={directory.isLoading ? "loading" : "finished"}
      loadingText="Loading Lusca Search users"
      empty="No Lusca Search users found"
      disabled={disabled}
      onFocus={() => setOpened(true)}
      onLoadItems={() => setOpened(true)}
      onChange={({ detail }) =>
        onChange(String(detail.selectedOption.value ?? ""))
      }
    />
  );
};

export default OwnerSelect;
